/**
 * Base API Client
 * Handles all HTTP requests with error handling, retries, and interceptors
 */

import { getDefaultHeaders, REQUEST_TIMEOUT, RETRY_CONFIG, API_ERROR_CODES } from './config';
import { XANO_API_GROUPS, XANO_HOST } from '../shared/constants';
import { z } from 'zod';

// Base API Error class
export class ApiError extends Error {
  statusCode: number;
  errorCode?: string;
  details?: unknown;

  constructor(
    message: string,
    statusCode: number,
    errorCode?: string,
    details?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
    this.statusCode = statusCode;
    this.errorCode = errorCode;
    this.details = details;
  }
}

// Generic API Response schema
export const ApiResponseSchema = <T extends z.ZodType>(dataSchema: T) =>
  z.object({
    success: z.boolean(),
    data: dataSchema.optional(),
    error: z.object({
      code: z.string(),
      message: z.string(),
      details: z.any().optional(),
    }).optional(),
    meta: z.object({
      timestamp: z.string(),
      requestId: z.string(),
      pagination: z.object({
        page: z.number(),
        limit: z.number(),
        total: z.number(),
        hasMore: z.boolean(),
      }).optional(),
    }).optional(),
  });

// Request configuration interface
interface RequestConfig {
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  headers?: HeadersInit;
  body?: unknown;
  params?: Record<string, string | number | boolean>;
  timeout?: number;
  retries?: number;
  requiresAuth?: boolean;
  validateResponse?: boolean;
  responseType?: 'json' | 'blob' | 'text';
}

// API Client class
class ApiClient {
  private baseURL: string;
  private authToken?: string;
  private interceptors: {
    request: Array<(config: RequestConfig) => RequestConfig | Promise<RequestConfig>>;
    response: Array<(response: Response) => Response | Promise<Response>>;
    error: Array<(error: ApiError) => void>;
  };

  constructor(baseURL: string) {
    this.baseURL = baseURL;
    this.interceptors = {
      request: [],
      response: [],
      error: [],
    };
  }

  /**
   * Set authentication token
   */
  setAuthToken(token: string | undefined) {
    this.authToken = token;
  }

  /**
   * Add request interceptor
   */
  addRequestInterceptor(interceptor: (config: RequestConfig) => RequestConfig | Promise<RequestConfig>) {
    this.interceptors.request.push(interceptor);
  }

  /**
   * Add response interceptor
   */
  addResponseInterceptor(interceptor: (response: Response) => Response | Promise<Response>) {
    this.interceptors.response.push(interceptor);
  }

  /**
   * Add error interceptor
   */
  addErrorInterceptor(interceptor: (error: ApiError) => void) {
    this.interceptors.error.push(interceptor);
  }

  /**
   * Build URL with query parameters
   */
  private buildURL(endpoint: string, params?: Record<string, string | number | boolean>): string {
    // Ensure baseURL ends with / and endpoint doesn't start with /
    const base = this.baseURL.endsWith('/') ? this.baseURL : `${this.baseURL}/`;
    const path = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
    const url = new URL(path, base);

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          url.searchParams.append(key, String(value));
        }
      });
    }

    return url.toString();
  }

  /**
   * Execute request with retries
   */
  private async executeWithRetry(
    url: string,
    config: RequestConfig,
    retryCount = 0
  ): Promise<Response> {
    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), config.timeout || REQUEST_TIMEOUT);

      const response = await fetch(url, {
        method: config.method,
        headers: config.headers,
        body: config.body ? JSON.stringify(config.body) : undefined,
        signal: controller.signal,
      });

      clearTimeout(timeout);

      if (!response.ok) {
        throw new ApiError(
          response.statusText || 'Request failed',
          response.status,
          undefined,
          await response.text()
        );
      }

      return response;
    } catch (error) {
      const maxRetries = config.retries ?? RETRY_CONFIG.maxRetries;
      
      if (
        retryCount < maxRetries &&
        error instanceof ApiError &&
        RETRY_CONFIG.retryableStatuses.includes(error.statusCode)
      ) {
        await new Promise(resolve => setTimeout(resolve, RETRY_CONFIG.retryDelay * (retryCount + 1)));
        return this.executeWithRetry(url, config, retryCount + 1);
      }
      
      throw error;
    }
  }

  /**
   * Main request method
   */
  async request<T>(
    endpoint: string,
    config: RequestConfig,
    responseSchema?: z.ZodType<T>
  ): Promise<T> {
    try {
      // Apply request interceptors
      let finalConfig = config;
      for (const interceptor of this.interceptors.request) {
        finalConfig = await interceptor(finalConfig);
      }

      // Add auth header if required
      if (finalConfig.requiresAuth && this.authToken) {
        finalConfig.headers = {
          ...getDefaultHeaders(this.authToken),
          ...finalConfig.headers,
        };
      } else {
        finalConfig.headers = {
          ...getDefaultHeaders(),
          ...finalConfig.headers,
        };
      }

      // Build URL
      const url = this.buildURL(endpoint, finalConfig.params);

      // Execute request
      let response = await this.executeWithRetry(url, finalConfig);

      // Apply response interceptors
      for (const interceptor of this.interceptors.response) {
        response = await interceptor(response);
      }

      // Parse response based on type
      let data: any;
      const responseType = finalConfig.responseType || 'json';
      
      if (responseType === 'blob') {
        data = await response.blob();
      } else if (responseType === 'text') {
        data = await response.text();
      } else {
        data = await response.json();
      }

      // Validate response if schema provided and response is JSON
      if (responseSchema && finalConfig.validateResponse !== false && responseType === 'json') {
        return responseSchema.parse(data);
      }

      return data;
    } catch (error) {
      // Transform error
      let apiError: ApiError;
      
      if (error instanceof ApiError) {
        apiError = error;
      } else if (error instanceof Error) {
        apiError = new ApiError(error.message, 0, API_ERROR_CODES.INTERNAL_ERROR);
      } else {
        apiError = new ApiError('Unknown error occurred', 0, API_ERROR_CODES.INTERNAL_ERROR);
      }

      // Apply error interceptors
      for (const interceptor of this.interceptors.error) {
        interceptor(apiError);
      }

      throw apiError;
    }
  }

  /**
   * Convenience methods
   */
  async get<T>(endpoint: string, options?: Omit<RequestConfig, 'method'>, schema?: z.ZodType<T>) {
    return this.request(endpoint, { ...options, method: 'GET' }, schema);
  }

  async post<T>(endpoint: string, body?: unknown, options?: Omit<RequestConfig, 'method' | 'body'>, schema?: z.ZodType<T>) {
    return this.request(endpoint, { ...options, method: 'POST', body }, schema);
  }

  async put<T>(endpoint: string, body?: unknown, options?: Omit<RequestConfig, 'method' | 'body'>, schema?: z.ZodType<T>) {
    return this.request(endpoint, { ...options, method: 'PUT', body }, schema);
  }

  async delete<T>(endpoint: string, options?: Omit<RequestConfig, 'method'>, schema?: z.ZodType<T>) {
    return this.request(endpoint, { ...options, method: 'DELETE' }, schema);
  }

  async patch<T>(endpoint: string, body?: unknown, options?: Omit<RequestConfig, 'method' | 'body'>, schema?: z.ZodType<T>) {
    return this.request(endpoint, { ...options, method: 'PATCH', body }, schema);
  }
}

// Build Xano API URL from base + API group + branch
const buildXanoApiUrl = (apiGroup: string): string => {
  const baseUrl = XANO_HOST;
  const branch = import.meta.env.VITE_XANO_BRANCH || '';
  const branchSuffix = branch ? `:${branch}` : '';
  return `${baseUrl}/api:${apiGroup}${branchSuffix}`;
};

// Create and export default client instance (using AUTH API group)
const apiClient = new ApiClient(buildXanoApiUrl(XANO_API_GROUPS.AUTH));

// Add global interceptors
apiClient.addRequestInterceptor(async (config) => {
  // Log requests in development
  if (import.meta.env.DEV) {
    // [API] Request logging disabled in production
  }
  return config;
});

apiClient.addResponseInterceptor(async (response) => {
  // Log responses in development
  if (import.meta.env.DEV) {
    // [API] Response logging disabled in production
  }
  return response;
});

apiClient.addErrorInterceptor((error) => {
  // Log errors
  // [API] Error logging disabled in production

  // Handle specific error codes
  if (error.errorCode === API_ERROR_CODES.TOKEN_EXPIRED) {
    // Trigger token refresh or logout
    window.dispatchEvent(new CustomEvent('auth:token-expired'));
  }
});

// Create Stripe client instance (using STRIPE API group)
const stripeClient = new ApiClient(buildXanoApiUrl(XANO_API_GROUPS.STRIPE));

// Add same interceptors as main client
stripeClient.addRequestInterceptor(async (config) => {
  if (import.meta.env.DEV) {
    // [API] Request logging disabled in production
  }
  return config;
});

stripeClient.addResponseInterceptor(async (response) => {
  if (import.meta.env.DEV) {
    // [API] Response logging disabled in production
  }
  return response;
});

stripeClient.addErrorInterceptor((error) => {
  // [API] Error logging disabled in production

  if (error.errorCode === API_ERROR_CODES.TOKEN_EXPIRED) {
    window.dispatchEvent(new CustomEvent('auth:token-expired'));
  }
});

// Create Exams client instance (using EXAMS API group)
const examsClient = new ApiClient(buildXanoApiUrl(XANO_API_GROUPS.EXAMS));

// Add same interceptors
examsClient.addRequestInterceptor(async (config) => {
  if (import.meta.env.DEV) {
    // [API] Request logging disabled in production
  }
  return config;
});

examsClient.addResponseInterceptor(async (response) => {
  if (import.meta.env.DEV) {
    // [API] Response logging disabled in production
  }
  return response;
});

examsClient.addErrorInterceptor((error) => {
  // [API] Error logging disabled in production

  if (error.errorCode === API_ERROR_CODES.TOKEN_EXPIRED) {
    window.dispatchEvent(new CustomEvent('auth:token-expired'));
  }
});

export { stripeClient, examsClient };
export default apiClient;